import { useEffect, useState } from 'react'

import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import { setPopupMessageModel } from '../../../redux/app/action'
import { useFormFn } from '../../../shared/antd/ANTDForm'
import debounce from '../../../utils/debounce'
import {
  entries,
  getNestedProperty,
  keys,
  length,
  notEqual,
} from '../../../utils/javascript'
import {
  addEquipmentApi,
  editEquipmentApi,
  einValidationApi,
} from '../../jobs/jobs.api'

const addEditEquipment = ({ editData, buildingId, onAddSuccess }) => {
  const { t } = useTranslations()
  const form = useFormFn()
  const { selector, dispatch } = useRedux()
  const catalogueState = selector(state => state?.catalogue?.selected)
  const { brandId, productSeriesId, modelId, unitType } = {
    ...catalogueState,
  }
  const [fileList, setFileList] = useState([])

  useEffect(() => {
    if (editData) {
      const { einInfo, imageDetails } = editData
      imageDetails?.fileUrl &&
        setFileList([
          {
            name: imageDetails?.fileName,
            url: imageDetails?.fileUrl,
            uid: imageDetails?.dmsId,
          },
        ])
      form.setFieldsValue({
        category: editData?.category,
        location: editData?.location,
        einName: einInfo?.name,
      })
    }
  }, [])

  useEffect(() => {
    form.setFieldsValue({ brandId, productSeriesId, modelId, unitType })
  }, [brandId, productSeriesId, modelId, unitType])

  const checkEINValidation = async ({ value }) => {
    const response = await einValidationApi({ params: `?value=${value}` })
    if (!response?.data?.success) {
      dispatch(
        setPopupMessageModel({
          open: true,
          message: 'msg_DuplicateEquipmentSerialNumber',
        }),
      )
      return false
    }
    return true
  }

  const debounceEINCheck = debounce(checkEINValidation, 400)

  const onSaveClick = async formValues => {
    const isValidEIN =
      !editData || notEqual(editData?.einInfo?.name, formValues?.einName)
        ? await checkEINValidation({
            value: formValues?.einName,
          })
        : true
    if (!isValidEIN) return
    const payload = { ...formValues, buildingId }
    delete payload.unitType
    let resp
    if (editData) {
      const keyToKey = {
        brandId: 'brandInfo.id',
        productSeriesId: 'productSeriesInfo.id',
        modelId: 'modelInfo.id',
        einName: 'einInfo.name',
        location: 'location',
        category: 'category',
        imageId: 'imageDetails.dmsId',
      }
      const editPayload = { equipmentId: editData?.equipmentId }
      entries(keyToKey)?.forEach(([k1, k2]) => {
        if (notEqual(payload[k1], getNestedProperty(editData, k2))) {
          editPayload[k1] = payload[k1]
          if (!editPayload[k1]) delete editPayload[k1]
        }
      })
      if (length(keys(editPayload)) < 2) return onAddSuccess()
      resp = await editEquipmentApi({ payload: editPayload })
    } else {
      resp = await addEquipmentApi({ payload })
    }
    if (!resp?.data) return
    onAddSuccess && onAddSuccess(resp?.data)
  }

  const handleValuesChange = value => {
    if (value?.einName) {
      debounceEINCheck({ value: value?.einName })
    }
  }

  const onFileUpload = ({ dmsId, file }) => {
    setFileList([file])
    form.setFieldValue('imageId', dmsId)
  }
  const onFileRemove = ({ dmsId }) => {
    setFileList([])
    form.setFieldValue('imageId', null)
  }

  const equipmentFormFields = {
    imageId: {
      inputType: 'image',
      fileList,
      onFileUpload,
      onFileRemove,
      uploadSingle: true,
      sm: 24,
      xs: 24,
    },
    location: {
      label: 'job_Location',
      inputType: 'input',
      required: true,
      sm: 24,
      xs: 24,
    },
    brandId: {
      label: 'txt_Brand',
      inputType: 'parentSelector',
      required: true,
      showBrand: true,
      withAdd: true,
      md: 8,
      sm: 12,
      xs: 24,
    },
    productSeriesId: {
      label: 'txt_ProductSeries',
      inputType: 'parentSelector',
      required: true,
      showProduct: true,
      withAdd: true,
      isAllProductSeries: true,
      md: 8,
      sm: 12,
      xs: 24,
    },
    unitType: {
      label: 'job_UnitType',
      inputType: 'parentSelector',
      required: true,
      showUnitType: true,
      withAdd: true,
      md: 8,
      sm: 12,
      xs: 24,
    },
    modelId: {
      label: 'txt_Model',
      validateTrigger: 'onChange',
      inputType: 'parentSelector',
      required: true,
      showModel: true,
      withAdd: true,
      md: 8,
      sm: 12,
      xs: 24,
    },
    category: {
      label: 'job_Category',
      inputType: 'select',
      options: [
        { label: 'Indoor', value: 'INDOOR' },
        { label: 'Outdoor', value: 'OUTDOOR' },
      ],
      required: true,
      md: 8,
      sm: 12,
      xs: 24,
    },
    einName: {
      label: 'job_EquipmentSerialNumber',
      validateTrigger: 'onChange',
      inputType: 'input',
      required: true,
      md: 8,
      sm: 12,
      xs: 24,
    },
  }

  return {
    t,
    form,
    equipmentFormFields,
    onSaveClick,
    handleValuesChange,
  }
}

export default addEditEquipment
